import {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import { Button, Form, Image, Input, Row, Typography, Upload } from 'antd';
import { useTranslation } from 'react-i18next';
import { EditOutlined, UploadOutlined } from '@ant-design/icons';
import {
  PaymentAccountRow,
  fetchPaymentAccount,
  upsertPaymentAccount
} from 'services/api/profile';

import Signature from '../Signature';
import { showRestError } from 'utils/showErrorMessage';
import { IconButton } from 'components/ui';

const { Text } = Typography;

interface IProps {
  onCompleted(): void;
  onError?(): void;
}

export type PaymentFieldsRef = {
  submit(): void;
  setEditSignature(value: boolean): void;
};

const PaymentFields: ForwardRefRenderFunction<PaymentFieldsRef, IProps> = (
  { onCompleted, onError },
  ref
) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [signature, setSignature] = useState<string | null>('');
  const [error, setError] = useState(false);
  const [editSignature, setEditSignature] = useState(false);
  const [paymentAccount, setPaymentAccount] =
    useState<PaymentAccountRow | null>(null);

  useEffect(() => {
    fetchPaymentAccount()
      .then(setPaymentAccount)
      .catch(() => setPaymentAccount(null));
  }, []);

  useEffect(() => {
    if (paymentAccount) {
      // GET returns the camelCase entity; the form fields + PUT DTO are
      // snake_case (hard-lesson L: camelCase read vs snake_case write).
      form.setFieldsValue({
        account_number: paymentAccount.accountNumber,
        bank_name: paymentAccount.bankName,
        billing_address: paymentAccount.billingAddress,
        holder_name: paymentAccount.holderName
      });
      setSignature(paymentAccount.signature);
    }
  }, [form, paymentAccount]);

  const onSubmit = data => {
    if (signature) {
      // v2 collapses create/update into a single PUT upsert
      upsertPaymentAccount({ ...data, signature })
        .then(() => onCompleted())
        .catch(err => {
          showRestError(err);
          onError?.();
        });
    } else {
      setError(true);
    }
  };

  const handleBeforeUpload = (file: File) => {
    return new Promise<File>(resolve => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64String = reader.result as string;
        setSignature(base64String);
        resolve(file);
        setError(false);
      };

      reader.readAsDataURL(file);
    });
  };

  const customRequest = onSuccess => {
    setTimeout(() => {
      onSuccess();
    }, 0);
  };

  useImperativeHandle(ref, () => ({
    submit: () => form.submit(),
    setEditSignature,
    setError
  }));

  return (
    <Form form={form} layout="vertical" onFinish={onSubmit} scrollToFirstError>
      <Form.Item
        label={t('profile.holder_name')}
        name="holder_name"
        messageVariables={{ label: t('profile.holder_name') }}
        rules={[{ required: true }]}
      >
        <Input
          size="large"
          placeholder={t('profile.holder_name_placeholder')}
        />
      </Form.Item>
      <Form.Item
        label={t('profile.bank_name')}
        name="bank_name"
        messageVariables={{
          label: t('profile.bank_name')
        }}
        rules={[{ required: true }]}
      >
        <Input size="large" placeholder={t('profile.bank_name_placeholder')} />
      </Form.Item>
      <Form.Item
        label={t('profile.account_number')}
        name="account_number"
        messageVariables={{
          label: t('profile.account_number')
        }}
        rules={[
          { required: true },
          {
            pattern: /\d+/,
            message: t('validation.only_numbers')
          }
        ]}
      >
        <Input size="large" placeholder="123412341234123" maxLength={40} />
      </Form.Item>
      <Form.Item
        label={t('profile.billing_address')}
        name="billing_address"
        messageVariables={{
          label: t('profile.billing_address')
        }}
        rules={[{ required: true }]}
      >
        <Input
          size="large"
          placeholder={t('profile.billing_address_placeholder')}
        />
      </Form.Item>
      <Row justify="space-between" align="middle">
        <Text style={{ fontWeight: 'bold' }}>{t('profile.signature')}</Text>
        {!!paymentAccount && !editSignature ? (
          <IconButton onClick={() => setEditSignature(true)}>
            <EditOutlined />
          </IconButton>
        ) : (
          <Upload
            multiple={false}
            maxCount={1}
            beforeUpload={handleBeforeUpload}
            customRequest={({ onSuccess }) => customRequest(onSuccess)}
            showUploadList={true}
            name="file"
          >
            <Button icon={<UploadOutlined />}>{t('profile.upload')}</Button>
          </Upload>
        )}
      </Row>
      {!!paymentAccount && !editSignature && !!signature ? (
        <Image src={signature} preview={false} />
      ) : (
        <Signature
          onClear={() => setSignature('')}
          onEnd={image => {
            setSignature(image);
            setError(false);
          }}
        />
      )}

      {error && <Text type="danger">{t('profile.signature_required')}</Text>}
    </Form>
  );
};

export default forwardRef(PaymentFields);
